import { ITable } from 'types'
import api, { APIError } from 'helpers/api'
import { config } from 'config'
import { DEFAULT_AVATAR_PATH, PROJECT } from 'app-constants'
declare global {
  interface Window {
    opera: any
    find: any
  }
}

export function getQueryParameter(name: string) {
  name = name.replace(/[\]\\]/g, '\\$&')
  const url = window.location.href
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)')
  const results = regex.exec(url)
  if (!results) return null
  if (!results[2]) return ''
  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

export function isValidEmail(email: string) {
  return /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    String(email).toLowerCase()
  )
}

export function capitalize(string: string) {
  return string.replace(/\b\w/g, (l) => l.toUpperCase())
}

export function debounce<F extends (...params: any[]) => any>(fn: F, delay: number) {
  let timeoutID: number
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutID)
    timeoutID = window.setTimeout(() => fn.apply(this, args), delay)
  } as F
}

export const isMortaUploadedUrl = (url: string): boolean => {
  const re =
    /^http(s)?:\/\/.*?\/v1\/files\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/gi
  return url.match(re) ? true : false
}

export const capitaliseFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const capitaliseEachWord = (string: string) => {
  return string.replace(/\b\w/g, (l) => l.toUpperCase())
}

export const getTable = (tables: ITable[], uid: string) => {
  try {
    for (let i = 0; i < tables.length; i++) {
      const publicId = tables[i].publicId
      if (publicId === uid) return tables[i]
    }
    return undefined
  } catch {
    return undefined
  }
}

export const getProjectTables = async (projectId: string, onlyAdmin?: boolean): Promise<ITable[]> => {
  const projectTables = []
  try {
    const projectResources = await api.getProjectResources(projectId, { excludeProcesses: true, onlyAdmin })

    for (let i = 0; i < projectResources.data.length; i++) {
      const projectResource = projectResources.data[i]
      if (projectResource.table) {
        projectTables.push(projectResource.table)
      }
    }
  } catch (e) {
    let message = `This ${PROJECT}'s tables couldn't be loaded. Refresh the page to try again.`

    if (e instanceof APIError) {
      const error = e as APIError
      if (error.message) {
        message = error.message
      } else {
        const errorEntry = error.errors![0]
        message =
          Array.isArray(errorEntry.messages) && errorEntry.messages.length > 0
            ? errorEntry.messages[0]
            : `This ${PROJECT}'s tables couldn't be loaded. Refresh the page to try again.`
      }
    } else if (typeof e === 'string') {
      message = e
    }

    return Promise.reject(message)
  }

  return projectTables
}

export const uuidv4 = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID() // Use the built-in secure UUID generator if available
  }
  const array = new Uint8Array(16)
  crypto.getRandomValues(array) // Generate secure random bytes
  array[6] = (array[6] & 0x0f) | 0x40 // Set version to 4
  array[8] = (array[8] & 0x3f) | 0x80 // Set variant to RFC 4122
  return [...array]
    .map((b, i) =>
      [4, 6, 8, 10].includes(i) ? '-' + b.toString(16).padStart(2, '0') : b.toString(16).padStart(2, '0')
    )
    .join('')
}

export const appUrl = (): string => {
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const port = window.location.port ? `:${window.location.port}` : ''
  return `${protocol}//${hostname}${port}`
}

const ALLOWED_HOSTS = new Set(['localhost', 'localhost:3000', 'localhost:5000', 'app.morta.io', 'api.morta.io'])
ALLOWED_HOSTS.add(window.location.host)
// Derive the API host from runtime config so any deployment environment
// automatically allows its own API subdomain.
try {
  const apiHost = new URL(config.API_HOST).host
  if (apiHost) ALLOWED_HOSTS.add(apiHost)
} catch {
  // config.API_HOST unset or invalid — fall back to the hardcoded list.
}

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'blob:'])

export function validateSourceUrl(raw?: string): string {
  if (!raw) return DEFAULT_AVATAR_PATH

  try {
    const u = new URL(raw, window.location.origin)

    // In-memory object URLs never leave the browser; allow them immediately.
    if (u.protocol === 'blob:') return raw

    if (
      ALLOWED_PROTOCOLS.has(u.protocol) &&
      ALLOWED_HOSTS.has(u.host) // exact host match
    ) {
      return u.href
    }
  } catch {
    return DEFAULT_AVATAR_PATH
  }

  return DEFAULT_AVATAR_PATH
}

/** 32-bit FNV-1a hash – tiny but good enough for keys */
export function hashString(str: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)
  }
  // Convert to unsigned and then to hex
  return (h >>> 0).toString(16)
}

export const isValidHttpUrl = (s: string) => {
  try {
    const u = new URL(s)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

// Allow URLs without a scheme (http/https) to be normalized to https://
export const normalizeProfileUrl = (raw?: string): string => {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return ''
  // Already carries a scheme (http/https or anything else like mailto:) —
  // leave it untouched and let the server validator have the final say.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

export const exportToCsv = (filename: string, headers: string[], rows: string[][]) => {
  const escapeCell = (cell: string) => {
    if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
      return `"${cell.replace(/"/g, '""')}"`
    }
    return cell
  }

  const csvContent = [headers.map(escapeCell).join(','), ...rows.map((row) => row.map(escapeCell).join(','))].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
